/**
 * Toucan Music: photographs of the physical record. They belong to one
 * edition, so this first chooses one (see {@link chooseRelease}). The choice
 * and its pictures go in the per-device cache, as in `prose.ts`.
 *
 * The page works without any of it: with no pictures, or while they load, it
 * shows Spotify's cover and no gallery.
 */

import { cached, putCached } from "toucan";
import { artworkOf, chooseRelease } from "./model.ts";
import type { Album, Artwork, ImageChoice, ReleaseChoice } from "./model.ts";
import { read } from "./musicbrainz.ts";
import { sleep } from "./pace.ts";

/** The Cover Art Archive, where every image below comes from. */
const COVER_ART = "https://coverartarchive.org";

const DAY = 24 * 60 * 60 * 1000;

/**
 * How long a fetched answer is used without refetching. Contributors add
 * scans, but a sleeve rarely changes.
 */
const FRESH = 30 * DAY;

/**
 * How long an answer is still shown. Past {@link FRESH} it is shown at once
 * and refetched in the background for next time; past this, the page waits
 * for the fetch.
 */
const KEEP = 400 * DAY;

/** What the chosen release adds to the page. */
export interface ReleaseDetail {
  /** The release this came from, for a link back to it. */
  release?: string;
  /**
   * Every picture the archive holds of it, in leafing order. Absent unless
   * there are at least two (see {@link addArtwork}).
   */
  artwork?: Artwork[];
}

interface ReleaseBrowse {
  releases?: {
    id: string;
    status?: string | null;
    date?: string | null;
    media?: { format?: string | null }[];
    "cover-art-archive"?: { front?: boolean; count?: number };
  }[];
}

interface Inventory {
  images?: {
    /** The scan as it was uploaded. */
    image?: string;
    types?: string[];
    /**
     * The archive's renderings of the scan, by width, or of a document's
     * first page. Older uploads lack some sizes.
     */
    thumbnails?: { "500"?: string; "1200"?: string; "250"?: string };
  }[];
}

/**
 * Everything the chosen release has to give, or `null` when a fetch failed
 * and nothing was cached. An empty answer is cached, but a failure isn't.
 */
export async function releaseDetail(
  album: Album,
): Promise<ReleaseDetail | null> {
  const group = album.ids.mbReleaseGroup;
  if (group === undefined) {
    return {};
  }
  // The number is the version of what is cached. Bump it whenever what is
  // derived changes, not only its shape: reading a different field of the
  // inventory, say, would otherwise leave cached records with no pictures.
  const key = `music:release:6:${group}`;
  const fresh = await cached<ReleaseDetail>(key, FRESH);
  if (fresh !== undefined) {
    return fresh;
  }
  const kept = await cached<ReleaseDetail>(key, KEEP);
  if (kept !== undefined) {
    // Refetch for next time. A failure is silent and keeps the cached answer.
    void (async () => {
      const found = await fetchDetail(group);
      if (found !== null) {
        await putCached(key, found);
      }
    })();
    return kept;
  }
  const detail = await fetchDetail(group);
  if (detail !== null) {
    await putCached(key, detail);
  }
  return detail;
}

/** Ask MusicBrainz and the archive, or `null` when either request fails. */
async function fetchDetail(group: string): Promise<ReleaseDetail | null> {
  const detail: ReleaseDetail = {};
  try {
    // The release is cached rather than stored with the album's ids, because
    // `chooseRelease` changes, and a stored choice would never be revisited.
    const release = await pickRelease(group);
    if (release !== undefined) {
      detail.release = release;
      await addArtwork(detail, release);
    }
  } catch {
    return null;
  }
  return detail;
}

/** Browse the group's editions and choose the one whose sleeve to show. */
async function pickRelease(group: string): Promise<string | undefined> {
  // A hundred is the endpoint's maximum.
  const browsed = await read<ReleaseBrowse>(
    `/release?release-group=${group}&inc=media&limit=100`,
  );
  // The release group was merged away or deleted.
  if (browsed === null) {
    return undefined;
  }
  const releases = (browsed.releases ?? []).map((release): ReleaseChoice => {
    const archive = release["cover-art-archive"];
    const choice: ReleaseChoice = {
      id: release.id,
      formats: (release.media ?? []).map((medium) => medium.format ?? ""),
      front: archive?.front === true,
      images: archive?.count ?? 0,
    };
    if (release.status !== undefined) {
      choice.status = release.status;
    }
    if (release.date !== undefined) {
      choice.date = release.date;
    }
    return choice;
  });
  return chooseRelease(releases);
}

/** How long to wait before retrying a busy archive, and how many tries. */
const ARCHIVE_RETRY = { wait: 1000, tries: 3 };

/**
 * Set the wait between tries, for tests that run with no waiting, and return
 * a function that restores the previous one.
 */
export function setArchiveRetryWaitForTests(wait: number): () => void {
  const previous = ARCHIVE_RETRY.wait;
  ARCHIVE_RETRY.wait = wait;
  return () => {
    ARCHIVE_RETRY.wait = previous;
  };
}

/**
 * What the archive holds for a release, or `null` on a 404. Under load it
 * answers with an error that usually clears a second later, so any other
 * error is retried and then thrown, since a "no pictures" answer would be
 * cached.
 */
async function inventoryOf(release: string): Promise<Inventory | null> {
  for (let attempt = 1; ; attempt += 1) {
    // The archive has no rate limit, so this skips MusicBrainz's pacer.
    const response = await fetch(`${COVER_ART}/release/${release}`);
    if (response.ok) {
      return (await response.json()) as Inventory;
    }
    if (response.status === 404) {
      return null;
    }
    if (attempt === ARCHIVE_RETRY.tries) {
      throw new Error(
        `The Cover Art Archive answered ${String(response.status)}`,
      );
    }
    await sleep(ARCHIVE_RETRY.wait);
  }
}

/**
 * Every picture the archive holds of this release, kept only when there are
 * two or more. With one picture, it is the front, which the page already shows
 * from Spotify.
 */
async function addArtwork(
  detail: ReleaseDetail,
  release: string,
): Promise<void> {
  const inventory = await inventoryOf(release);
  // Nobody has scanned this release.
  if (inventory === null) {
    return;
  }
  const images = (inventory.images ?? []).flatMap((image): ImageChoice[] => {
    // The scan as uploaded, the one address every record has. Older records
    // lack some of the thumbnail sizes.
    const url = image.image;
    if (url === undefined) {
      return [];
    }
    const choice: ImageChoice = { url: secure(url) };
    if (image.types !== undefined) {
      choice.types = image.types;
    }
    // An `<img>` can't show a PDF, so the card shows the archive's rendering
    // of its first page and keeps the PDF for the lightbox. A PDF with no
    // rendering is left out.
    if (/\.pdf$/i.test(url)) {
      const page =
        image.thumbnails?.["500"] ??
        image.thumbnails?.["1200"] ??
        image.thumbnails?.["250"];
      if (page === undefined) {
        return [];
      }
      choice.pdf = choice.url;
      choice.url = secure(page);
    }
    return [choice];
  });
  if (images.length > 1) {
    detail.artwork = artworkOf(images);
  }
}

/**
 * The same address over https. The archive's older records give `http://`
 * URLs, which the browser blocks as mixed content without saying so.
 */
function secure(url: string): string {
  return url.replace(/^http:/, "https:");
}
